import React, { useState, useEffect } from 'react';
import { getStatistics } from '../services/api';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, AreaChart, Area
} from 'recharts';
import { FiUsers, FiDollarSign, FiMapPin, FiHome, FiTrendingUp, FiTarget } from 'react-icons/fi';

const COLORS = ['#1565C0', '#4CAF50', '#FF9800', '#f44336', '#9C27B0', '#009688', '#FF5722', '#3F51B5', '#795548', '#607D8B', '#E91E63', '#00BCD4'];
const MONTHS_FR = ['', 'Jan', 'Fev', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Aout', 'Sep', 'Oct', 'Nov', 'Dec'];

const fmt = (n: number) => new Intl.NumberFormat('fr-FR').format(n);
const fmtM = (n: number) => {
  if (n >= 1000000000) return (n / 1000000000).toFixed(1) + ' Md';
  if (n >= 1000000) return (n / 1000000).toFixed(1) + ' M';
  if (n >= 1000) return (n / 1000).toFixed(0) + ' k';
  return String(n);
};
const pct = (a: number, b: number) => b > 0 ? Math.round((a / b) * 100) : 0;

const Statistics = () => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getStatistics().then(r => setData(r.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading">Chargement des statistiques...</div>;
  if (!data) return <div className="alert alert-error">Impossible de charger les statistiques</div>;

  const { kpis, versementsMois, versementsType, versementsProjet, parcellesProjet, topCotisants, membresEvolution } = data;

  // Chart data transforms
  const chartMois = (versementsMois || []).map((v: any) => {
    const [y, m] = v.mois.split('-');
    return { mois: MONTHS_FR[parseInt(m)] + ' ' + y.slice(2), total: parseInt(v.total), nb: parseInt(v.nb) };
  });

  const chartType = (versementsType || []).map((v: any) => ({
    name: v.libelle, value: parseInt(v.total)
  }));

  const chartProjet = (versementsProjet || []).map((v: any) => ({
    name: v.projet, total: parseInt(v.total)
  }));

  const chartMembresEvo = (membresEvolution || []).map((v: any) => {
    const [y, m] = v.mois.split('-');
    return { mois: MONTHS_FR[parseInt(m)] + ' ' + y.slice(2), nb: parseInt(v.nb) };
  });

  return (
    <div>
      <h1 className="page-title">Statistiques & Reporting</h1>

      {/* ===== KPI CARDS ===== */}
      <div className="stats-kpi-grid">
        <div className="stats-kpi" style={{ borderLeftColor: '#1565C0' }}>
          <div className="stats-kpi-icon" style={{ background: '#e3f2fd', color: '#1565C0' }}><FiUsers /></div>
          <div className="stats-kpi-body">
            <div className="stats-kpi-value">{fmt(kpis.nbMembres)}</div>
            <div className="stats-kpi-label">Membres</div>
            <div className="stats-kpi-sub">{fmt(kpis.nbHommes)} H / {fmt(kpis.nbFemmes)} F</div>
          </div>
        </div>
        <div className="stats-kpi" style={{ borderLeftColor: '#4CAF50' }}>
          <div className="stats-kpi-icon" style={{ background: '#e8f5e9', color: '#2e7d32' }}><FiDollarSign /></div>
          <div className="stats-kpi-body">
            <div className="stats-kpi-value">{fmtM(kpis.versements.total)}</div>
            <div className="stats-kpi-label">Total Versements</div>
            <div className="stats-kpi-sub">{fmt(kpis.versements.nb)} versements</div>
          </div>
        </div>
        <div className="stats-kpi" style={{ borderLeftColor: '#FF9800' }}>
          <div className="stats-kpi-icon" style={{ background: '#fff3e0', color: '#e65100' }}><FiMapPin /></div>
          <div className="stats-kpi-body">
            <div className="stats-kpi-value">{fmt(kpis.parcelles.total)}</div>
            <div className="stats-kpi-label">Parcelles</div>
            <div className="stats-kpi-sub">{fmt(kpis.parcelles.attribuees)} attrib. / {fmt(kpis.parcelles.non_attribuees)} dispo.</div>
          </div>
        </div>
        <div className="stats-kpi" style={{ borderLeftColor: '#f44336' }}>
          <div className="stats-kpi-icon" style={{ background: '#fce4ec', color: '#c62828' }}><FiHome /></div>
          <div className="stats-kpi-body">
            <div className="stats-kpi-value">{fmt(kpis.nbProjets)}</div>
            <div className="stats-kpi-label">Projets</div>
            <div className="stats-kpi-sub">Superficie: {fmt(Math.round(kpis.parcelles.superficie_totale))} m²</div>
          </div>
        </div>
        <div className="stats-kpi" style={{ borderLeftColor: '#9C27B0' }}>
          <div className="stats-kpi-icon" style={{ background: '#f3e5f5', color: '#7b1fa2' }}><FiTrendingUp /></div>
          <div className="stats-kpi-body">
            <div className="stats-kpi-value">{fmtM(kpis.versements.moyenne)}</div>
            <div className="stats-kpi-label">Versement moyen</div>
            <div className="stats-kpi-sub">Min: {fmt(kpis.versements.min_montant)} / Max: {fmt(kpis.versements.max_montant)}</div>
          </div>
        </div>
        <div className="stats-kpi" style={{ borderLeftColor: '#009688' }}>
          <div className="stats-kpi-icon" style={{ background: '#e0f2f1', color: '#00695c' }}><FiTarget /></div>
          <div className="stats-kpi-body">
            <div className="stats-kpi-value">{fmtM(kpis.solde)}</div>
            <div className="stats-kpi-label">Solde (Vers. - Charges)</div>
            <div className="stats-kpi-sub">Charges: {fmtM(kpis.charges.total)} FCFA</div>
          </div>
        </div>
      </div>

      {/* ===== ROW 1: Evolution + Type =====*/}
      <div className="stats-row">
        <div className="stats-chart-card" style={{ flex: 2 }}>
          <h3>Evolution des cotisations (12 mois)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={chartMois}>
              <defs>
                <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#1565C0" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#1565C0" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="mois" fontSize={11} />
              <YAxis fontSize={11} tickFormatter={v => fmtM(v)} />
              <Tooltip formatter={(v: number) => fmt(v) + ' FCFA'} />
              <Area type="monotone" dataKey="total" stroke="#1565C0" fillOpacity={1} fill="url(#colorTotal)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="stats-chart-card" style={{ flex: 1 }}>
          <h3>Par type de paiement</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie data={chartType} cx="50%" cy="50%" outerRadius={90} innerRadius={40} dataKey="value" label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`} labelLine={false} fontSize={10}>
                {chartType.map((_: any, i: number) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v: number) => fmt(v) + ' FCFA'} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ===== ROW 2: Par Projet ===== */}
      <div className="stats-row">
        <div className="stats-chart-card" style={{ flex: 1 }}>
          <h3>Versements par projet</h3>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={chartProjet} layout="vertical" margin={{ left: 80 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" fontSize={11} tickFormatter={v => fmtM(v)} />
              <YAxis type="category" dataKey="name" fontSize={11} width={75} />
              <Tooltip formatter={(v: number) => fmt(v) + ' FCFA'} />
              <Bar dataKey="total" fill="#FF9800" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ===== ROW 3: Evolution inscriptions ===== */}
      <div className="stats-row">
        <div className="stats-chart-card" style={{ flex: 1 }}>
          <h3>Inscriptions par mois</h3>
          <ResponsiveContainer width="100%" height={320}>
            <LineChart data={chartMembresEvo}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="mois" fontSize={11} />
              <YAxis fontSize={11} />
              <Tooltip />
              <Line type="monotone" dataKey="nb" stroke="#9C27B0" strokeWidth={2} dot={{ r: 4 }} name="Inscriptions" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ===== PARCELLES PAR PROJET ===== */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-header">Parcelles par projet</div>
        <div className="card-body">
          <div className="table-responsive">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Projet</th>
                  <th style={{ textAlign: 'right' }}>Total</th>
                  <th style={{ textAlign: 'right' }}>Attribuées</th>
                  <th style={{ textAlign: 'right' }}>Non attrib.</th>
                  <th style={{ textAlign: 'right' }}>Superficie (m²)</th>
                  <th style={{ width: 150 }}>Taux attrib.</th>
                </tr>
              </thead>
              <tbody>
                {(parcellesProjet || []).map((p: any, i: number) => {
                  const attribuables = p.total - p.non_attribuables;
                  const taux = pct(p.attribuees, attribuables);
                  return (
                    <tr key={i}>
                      <td><strong>{p.projet}</strong></td>
                      <td style={{ textAlign: 'right' }}>{fmt(p.total)}</td>
                      <td style={{ textAlign: 'right' }}>{fmt(p.attribuees)}</td>
                      <td style={{ textAlign: 'right' }}>{fmt(attribuables - p.attribuees)}</td>
                      <td style={{ textAlign: 'right' }}>{fmt(Math.round(p.superficie))}</td>
                      <td>
                        <div style={{ background: '#f5f5f5', borderRadius: 6, height: 18, overflow: 'hidden', position: 'relative' }}>
                          <div style={{ background: '#1565C0', height: '100%', width: taux + '%', borderRadius: 6 }} />
                          <span style={{ position: 'absolute', right: 6, top: 1, fontSize: 11, fontWeight: 600 }}>{taux}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ===== TOP COTISANTS ===== */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-header">Top 10 cotisants</div>
        <div className="card-body">
          <div className="table-responsive">
            <table className="data-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Membre</th>
                  <th>Matricule</th>
                  <th style={{ textAlign: 'right' }}>Nb versements</th>
                  <th style={{ textAlign: 'right' }}>Total (FCFA)</th>
                </tr>
              </thead>
              <tbody>
                {(topCotisants || []).map((c: any, i: number) => (
                  <tr key={i}>
                    <td><span className="badge badge-blue">{i + 1}</span></td>
                    <td><strong>{c.prenomnom}</strong></td>
                    <td>{c.matricule || '-'}</td>
                    <td style={{ textAlign: 'right' }}>{fmt(c.nb)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmt(c.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Statistics;
